import Analytics from "analytics";
import googleAnalytics from "@analytics/google-analytics";
import { storage } from "services/storage";

/**
 * Central Google Analytics 4 (GA4) configuration for the quiz-game app.
 *
 * Mirrors the assessment app's analytics config. Sends data to GA4 using the
 * Measurement ID from REACT_APP_GA_MEASUREMENT_ID.
 *
 * The quiz-game runs inside the assessment app's game iframe, so we identify by
 * the same gameplay/link id — this ties in-game events (audio recorded /
 * re-recorded / failed) to the same candidate the parent app identified.
 *
 * If REACT_APP_GA_MEASUREMENT_ID is missing, analytics runs as a no-op so the
 * app never breaks.
 */

const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;

const isEnabled = Boolean(measurementId);

const analytics = Analytics({
  app: "Unberry Quiz Game",
  debug: process.env.NODE_ENV !== "production",
  plugins: isEnabled
    ? [
        googleAnalytics({
          measurementIds: [measurementId],
        }),
      ]
    : [],
});

// Identify by the gameplay/link id so in-game events attach to the same
// candidate the parent assessment app identified. This enables per-candidate
// timelines in GA4's User Explorer.
//
// IMPORTANT: only opaque IDs are sent — GA4's Terms of Service forbid PII
// (email / name). Map ids back to real people via your own backend.
export const identifyCandidate = () => {
  try {
    const linkId = storage.get.gameplayId();
    const user = storage.get.user();
    if (linkId) {
      analytics.identify(linkId, {
        candidate_id: user?._id ?? user?.id ?? linkId,
        link_id: linkId,
        org: storage.get.org(),
        game_id: storage.get.gameId(),
      });
    }
  } catch (e) {
    // storage may be unavailable; never let analytics break the app
  }
};

if (!isEnabled) {
  // eslint-disable-next-line no-console
  console.warn(
    "[analytics] REACT_APP_GA_MEASUREMENT_ID not set — GA4 tracking is disabled (no-op)."
  );
}

export default analytics;
